import { Component, OnInit, OnDestroy } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { DataService } from "src/app/shared/Services/data.service";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";

@Component({
  selector: "app-escalation",
  templateUrl: "./escalation.component.html",
  styleUrls: ["./escalation.component.scss"],
})
export class EscalationComponent implements OnInit, OnDestroy {
  breadCrumbItems: Array<{}>;
  searchTerm: any = "";
  currentPage = 1;
  itemsPerPage = 3;
  pageSize = 0;
  errmsg = "";
  custId: string;
  userData: any
  data: any[] = [];
  departmentData: any[] = [];
  deviceData: any[] = [];
  devicelistData: any[] = [];

  isEditMode = false;
  editIndex: number | null = null;

  deleteIndex: number | null = null;
  deleteItem_data: any = null;

  // ── Hardcoded Parameters ───────────────────────────────────────────────────
  parameterList: string[] = ["I_R", "I_Y", "I_B", "I_TOTAL","PF_R","PF_Y","PF_B","VN_R","VN_Y","VN_B"];

  // ── Form Model ─────────────────────────────────────────────────────────────
  formModel = {
    departmentName: "",
    deviceName: "",
    deviceId: "",
    parameter: "",
    alert: false,
    alertOccurence: null,
    alertmsg: "",
    isInterlock: false,
    alertDuration: null,
    interlockDuration: null,
    upperLimit: null,
    lowerLimit: null,
  };

  // ── Validation errors ──────────────────────────────────────────────────────
  formErrors: any = {};

  private destroy$ = new Subject<void>();

  constructor(
    private dataservice: DataService,
    private toastr: ToastrService,
    private modalService: NgbModal,
  ) {
    this.userData = JSON.parse(sessionStorage.getItem("currentUser"));
    this.custId = this.userData?.user?.custId;
  }

  ngOnInit(): void {
    this.breadCrumbItems = [
      { label: "Configuration" },
      { label: "Escalation", active: true },
    ];
    this.loadDepartments();
    this.getData();
  }

  // ── Load Departments ───────────────────────────────────────────────────────
  loadDepartments() {
    this.dataservice
      .get("department")
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        if (res?.data?.length) {
          this.departmentData = res.data;
          this.loadDevices();
        }
      });
  }

  // ── Load Devices ───────────────────────────────────────────────────────────
  loadDevices() {
    this.dataservice
      .get("api/device")
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res: any) => {
          if (res?.length) {
            this.deviceData = res.filter((val:any)=>val.meterSupplier==="K2-V1.1");
          }
        },
        () => this.toastr.error("Server Unreachable", "Error"),
      );
  }

  // ── Cascading Select ───────────────────────────────────────────────────────
  onDepartmentChange(selectedDeptName: string) {
    this.devicelistData = this.deviceData.filter(
      (d) => d.departmentName === selectedDeptName,
    );
    this.formModel.deviceName = "";
    this.formModel.deviceId = "";
  }

  onDeviceChange(selectedDeviceName: string) {
    const selectedDevice = this.devicelistData.find(
      (d) => d.deviceName === selectedDeviceName,
    );
    if (selectedDevice) {
      this.formModel.deviceId = selectedDevice.deviceId;
    }
  }

  // ── Alert toggle: clear dependent fields when turned off ──────────────────
  onAlertToggle() {
    if (!this.formModel.alert) {
      this.formModel.alertOccurence = null;
      this.formErrors.alertOccurence = null;
    }
  }

  // ── Interlock toggle: clear dependent fields when turned off ───────────────
  onInterlockToggle() {
    if (!this.formModel.isInterlock) {
      this.formModel.interlockDuration = null;
      this.formErrors.interlockDuration = null;
      this.formErrors.interlockDuration = null;
    }
  }

  // ── Load Table Data ────────────────────────────────────────────────────────
  getData() {
    this.dataservice
      .get(`api/esculation?custId=${this.custId}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res: any) => {
          this.data = res;
        },
        (err) => {
          this.toastr.error('Server Unreachable');
          this.errmsg = 'Failed to load Escalation Data.';
        }
      );
  }

  onPageChange(pageNum: number): void {
    this.pageSize = this.itemsPerPage * (pageNum - 1);
  }

  // ── Reset form model ───────────────────────────────────────────────────────
  resetForm() {
    this.formModel = {
      departmentName: "",
      deviceName: "",
      deviceId: "",
      parameter: "",
      alert: false,
      alertOccurence: null,
      isInterlock: false,
      interlockDuration: null,
      alertDuration: null,
      alertmsg: "",
      upperLimit: null,
      lowerLimit: null,
    };
    this.formErrors = {};
    this.devicelistData = [];
  }

  // ── Open ADD Modal ─────────────────────────────────────────────────────────
  openAddModal(content: any) {
    this.isEditMode = false;
    this.editIndex = null;
    this.resetForm();
    this.modalService.open(content, {
      backdrop: "static",
      centered: true,
      size: "lg",
    });
  }

  // ── Open EDIT Modal ────────────────────────────────────────────────────────
  openEditModal(item: any, index: number, content: any) {
    this.isEditMode = true;
    this.editIndex = index;
    this.formErrors = {};

    // Populate cascaded device list for this department
    this.devicelistData = this.deviceData.filter(
      (d) => d.departmentName === item.departmentName,
    );

    // Clone item into formModel
    this.formModel = {
      ...item,
      isInterlock: item.isInterlock === "true" ? true : !!item.isInterlock,
    };

    this.modalService.open(content, {
      backdrop: "static",
      centered: true,
      size: "lg",
    });
  }

  // ── Manual Validation ──────────────────────────────────────────────────────
  validateForm(): boolean {
    this.formErrors = {};
    let valid = true;

    if (!this.formModel.departmentName) {
      this.formErrors.departmentName = "Department is required.";
      valid = false;
    }
    if (!this.formModel.deviceName) {
      this.formErrors.deviceName = "Machine Name is required.";
      valid = false;
    }
    if (!this.formModel.parameter) {
      this.formErrors.parameter = "Parameter is required.";
      valid = false;
    }
    if (
      this.formModel.upperLimit === null ||
      this.formModel.upperLimit === ""
    ) {
      this.formErrors.upperLimit = "Upper Limit is required.";
      valid = false;
    } else if (this.formModel.upperLimit < 0) {
      this.formErrors.upperLimit = "Must be 0 or greater.";
      valid = false;
    }
    if (
      this.formModel.lowerLimit === null ||
      this.formModel.lowerLimit === ""
    ) {
      this.formErrors.lowerLimit = "Lower Limit is required.";
      valid = false;
    } else if (this.formModel.lowerLimit < 0) {
      this.formErrors.lowerLimit = "Must be 0 or greater.";
      valid = false;
    }
    if (this.formModel.alert) {
      if (
        !this.formModel.alertOccurence ||
        (this.formModel.alertOccurence <= 1 &&
          this.formModel.alertOccurence >= 5)
      ) {
        this.formErrors.alertOccurence = "Alert Occurrence must be at least 1.";
        valid = false;
      }
      if (!this.formModel.alertDuration || this.formModel.alertDuration < 1) {
        this.formErrors.alertDuration = "Duration must be at least 1.";
        valid = false;
      }
      if (!this.formModel.alertmsg) {
        this.formErrors.alertmsg = "Aler Message Needed";
        valid = false;
      }
    }
    if (this.formModel.isInterlock) {
      if (
        !this.formModel.interlockDuration ||
        this.formModel.interlockDuration < 1
      ) {
        this.formErrors.interlockDuration =
          "Interlock Duration must be at least 1.";
        valid = false;
      }
    }

    return valid;
  }

  // ── CREATE / UPDATE ────────────────────────────────────────────────────────
  onSubmit(modal: any) {
    if (!this.validateForm()) return;

    const payload = { ...this.formModel };

    if (this.isEditMode && this.editIndex !== null) {
      this.dataservice.put(`api/esculation`, payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          () => {
            this.toastr.success("Record updated successfully", "Success");
            this.modalService.dismissAll();
            this.getData();
            modal.close();
            this.resetForm();
          },
          (err) => this.toastr.error(err.message)
        );

    } else {
      this.dataservice.post('api/esculation', payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          () => {
            this.toastr.success("Record updated successfully", "Success");
            this.modalService.dismissAll();
            this.getData();
            modal.close();
            this.resetForm();
          },
          (err) => this.toastr.error(err.message)
        );
    }


  }

  // ── DELETE ─────────────────────────────────────────────────────────────────
  openDeleteModal(item: any, index: number, content: any) {
    this.deleteIndex = index;
    this.deleteItem_data = item;
    this.modalService.open(content, { backdrop: "static", centered: true });
  }

  confirmDelete(modal: any) {
    if (this.deleteIndex !== null) {

      this.dataservice
        .delete(`api/esculation?id=${this.deleteItem_data.parameterId}&deviceId=${this.deleteItem_data.deviceId}`)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          () => {
            this.toastr.success('Escalation deleted successfully');
            this.modalService.dismissAll();
            this.getData();
          },
          () => this.toastr.error('Failed to delete Escalation')
        );
      // ── Auto go to previous page if current page is now empty ──────────
      const filteredLength = this.data.length;
      const totalPages = Math.ceil(filteredLength / this.itemsPerPage);

      if (this.currentPage > totalPages && totalPages > 0) {
        this.currentPage = totalPages;
        this.onPageChange(this.currentPage);
      } else if (totalPages === 0) {
        this.currentPage = 1;
        this.pageSize = 0;
      }

      this.deleteIndex = null;
      this.deleteItem_data = null;
      modal("Cross click");
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
